import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wine, Activity, ShoppingBag, Minus, Plus } from "lucide-react";
import { getProducts, addConsumptionsBatch, getReservation, fmt } from "@/lib/api";

const CATS = [
  { id: "bar", label: "Bar", icon: Wine },
  { id: "activities", label: "Activities", icon: Activity },
  { id: "shop", label: "Shop", icon: ShoppingBag },
];

export default function AddConsumptionDialog({ reservationId, open, onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({}); // {productId: {product, qty, price}}
  const [tab, setTab] = useState("bar");
  const [unitType, setUnitType] = useState(null);

  useEffect(() => {
    if (open) {
      setCart({});
      setTab("bar");
      getProducts().then(setProducts).catch(() => {});
      if (reservationId) {
        getReservation(reservationId)
          .then(r => setUnitType(r?.unit?.unit_type || null))
          .catch(() => setUnitType(null));
      }
    }
  }, [open, reservationId]);

  const priceFor = (p) => {
    if (unitType && p.variant_prices && p.variant_prices[unitType] != null) return p.variant_prices[unitType];
    return p.price;
  };

  const filtered = products.filter(p => p.category === tab);

  const addToCart = (p) => {
    const resolved = priceFor(p);
    setCart(prev => {
      const current = prev[p.id];
      return {
        ...prev,
        [p.id]: {
          product: p,
          qty: (current?.qty || 0) + 1,
          price: current?.price ?? resolved,
        },
      };
    });
  };

  const decrement = (pid) => {
    setCart(prev => {
      const c = prev[pid];
      if (!c) return prev;
      if (c.qty <= 1) { const n = { ...prev }; delete n[pid]; return n; }
      return { ...prev, [pid]: { ...c, qty: c.qty - 1 } };
    });
  };

  const changePrice = (pid, newPrice) => {
    setCart(prev => ({ ...prev, [pid]: { ...prev[pid], price: parseFloat(newPrice) || 0 } }));
  };

  const total = Object.values(cart).reduce((s, c) => s + c.qty * c.price, 0);
  const cartArr = Object.values(cart);

  const handleSave = async () => {
    if (cartArr.length === 0) return;
    try {
      await addConsumptionsBatch({
        reservation_id: reservationId,
        items: cartArr.map(c => ({
          product_id: c.product.id,
          quantity: c.qty,
          unit_price: c.price,
        })),
      });
      toast.success(`Added ${cartArr.length} item(s)`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Failed to add consumption");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="add-consumption-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Consumption</DialogTitle>
          <DialogDescription className="sr-only">Pick products and quantities to add to this reservation.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-3">
            {CATS.map(c => {
              const Icon = c.icon;
              return (
                <TabsTrigger key={c.id} value={c.id} data-testid={`consumption-tab-${c.id}`}>
                  <Icon size={14} className="mr-1.5" strokeWidth={1.5} /> {c.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {CATS.map(c => (
            <TabsContent key={c.id} value={c.id} className="mt-4">
              <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto pr-1">
                {filtered.map(p => {
                  const inCart = cart[p.id];
                  return (
                    <button
                      key={p.id}
                      onClick={() => addToCart(p)}
                      data-testid={`product-btn-${p.id}`}
                      className={`pos-btn h-20 rounded-md border bg-white text-left p-3 flex flex-col justify-between hover:border-blue-500 hover:bg-blue-50 ${inCart ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}
                    >
                      <div className="text-sm font-medium text-slate-900 leading-tight">
                        {p.name}
                        {p.variant_prices && Object.keys(p.variant_prices).length > 0 && <span className="ml-1 text-[8px] font-semibold text-blue-600 uppercase tracking-wider">· dyn</span>}
                      </div>
                      <div className="flex items-end justify-between">
                        <div className="text-xs text-slate-500">{fmt(priceFor(p))}</div>
                        {inCart && <div className="text-xs font-bold text-blue-600">×{inCart.qty}</div>}
                      </div>
                    </button>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="col-span-3 text-sm text-slate-400 text-center py-8">No products in this category</div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {cartArr.length > 0 && (
          <div className="border-t border-slate-200 pt-3 max-h-[200px] overflow-y-auto">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Cart</div>
            <div className="space-y-1.5">
              {cartArr.map(c => (
                <div key={c.product.id} className="flex items-center gap-2 text-sm" data-testid={`cart-row-${c.product.id}`}>
                  <div className="flex-1 font-medium text-slate-900">{c.product.name}</div>
                  <button onClick={() => decrement(c.product.id)} className="h-7 w-7 flex items-center justify-center border border-slate-200 rounded hover:bg-slate-100">
                    <Minus size={12} />
                  </button>
                  <div className="w-8 text-center font-semibold">{c.qty}</div>
                  <button onClick={() => addToCart(c.product)} className="h-7 w-7 flex items-center justify-center border border-slate-200 rounded hover:bg-slate-100">
                    <Plus size={12} />
                  </button>
                  <Input
                    type="number"
                    step="0.01"
                    value={c.price}
                    onChange={(e) => changePrice(c.product.id, e.target.value)}
                    className="w-20 h-8"
                  />
                  <div className="w-16 text-right font-semibold text-slate-900">{fmt(c.qty * c.price)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="flex-row justify-between items-center pt-3 border-t border-slate-200">
          <div className="text-sm">
            <span className="text-slate-500">Total: </span>
            <span className="font-heading text-xl font-bold text-slate-900" data-testid="consumption-dialog-total">{fmt(total)}</span>
          </div>
          <Button
            onClick={handleSave}
            disabled={cartArr.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-6"
            data-testid="consumption-dialog-save"
          >
            Add to Bill
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
